import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { UserProfile, SubscriptionTier } from '@/types';
import { useAppStore } from '@/store';
import { Profile as ProfileType } from '@/types/supabase';
import { v4 as uuidv4 } from 'uuid';

export function useProfile() {
  const { user: authUser } = useAuth();
  const { user: profile, setUser: setProfile } = useAppStore();
  const isMounted = useRef(true);

  const fetchProfile = useCallback(async (userId: string) => {
    console.log('useProfile: fetchProfile initiated for userId:', userId);
    try {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('useProfile: Error fetching profile data:', profileError);
        throw profileError;
      }

      let finalProfileData = profileData;

      if (!finalProfileData) {
        console.log('useProfile: No profile found for user, creating a new one.');
        const { data: newProfileData, error: newProfileError } = await supabase
          .from('profiles')
          .insert({ id: userId, full_name: authUser?.email?.split('@')[0] || 'User', email: authUser?.email })
          .select('*')
          .single();

        if (newProfileError) {
          console.error('useProfile: Error creating new profile:', newProfileError);
          throw newProfileError;
        }
        console.log('useProfile: New profile created:', newProfileData);
        finalProfileData = newProfileData;
      }

      console.log('useProfile: Fetched profile data:', finalProfileData);

      const { data: subData, error: subError } = await supabase
        .from('user_subscriptions')
        .select('plan_id, status')
        .eq('user_id', userId)
        .eq('status', 'active')
        .order('started_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (subError) {
        console.error('useProfile: Error fetching subscription:', subError);
      }
      console.log('useProfile: Fetched subscription data:', subData);

      const userProfile: UserProfile = {
        id: userId,
        email: authUser?.email || '',
        name: finalProfileData?.full_name || authUser?.email?.split('@')[0] || 'User',
        avatar: finalProfileData?.avatar_url || undefined,
        subscription: (subData?.plan_id as SubscriptionTier) || 'free',
        defaultModelApiName: finalProfileData?.default_model || undefined,
        role: finalProfileData?.role || 'member',
      };

      if (isMounted.current) {
        setProfile(userProfile);
        console.log('useProfile: Global profile state updated:', userProfile);
      }
    } catch (error) {
      console.error('useProfile: Error in fetchProfile:', error);
      if (isMounted.current) {
        setProfile(null);
        console.log('useProfile: Global profile state cleared due to error.');
      }
    }
  }, [authUser, setProfile]);

  const updateProfile = useCallback(async (updates: Partial<ProfileType>) => {
    if (!authUser?.id) {
      console.error('useProfile: User not authenticated for updateProfile.');
      return { success: false, error: new Error('User not authenticated.') };
    }

    try {
      const dataToUpsert = { ...updates, id: authUser.id };
      console.log('useProfile: Attempting to update profile with:', dataToUpsert);

      const { data, error } = await supabase
        .from('profiles')
        .upsert(dataToUpsert, { onConflict: 'id' })
        .select()
        .single();

      if (error) {
        console.error('useProfile: Error updating profile:', error);
        return { success: false, error };
      }

      if (data) {
        if (isMounted.current) {
          setProfile(prevProfile => {
            const updatedProfile = {
              ...(prevProfile || {} as UserProfile),
              ...data,
              name: data.full_name || prevProfile?.name,
              avatar: data.avatar_url || prevProfile?.avatar,
              defaultModelApiName: data.default_model || prevProfile?.defaultModelApiName,
            };
            console.log('useProfile: Global profile state updated after updateProfile:', updatedProfile);
            return updatedProfile;
          });
        }
        return { success: true, data };
      }
      return { success: false, error: new Error('No data returned after upsert.') };
    } catch (error) {
      console.error('useProfile: Exception updating profile:', error);
      return { success: false, error: error as Error };
    }
  }, [authUser, setProfile]);

  const uploadAvatar = useCallback(async (file: File) => {
    if (!authUser?.id) {
      console.error('useProfile: User not authenticated for uploadAvatar.');
      return { success: false, error: new Error('User not authenticated.') };
    }

    const fileExtension = file.name.split('.').pop();
    const filePath = `${authUser.id}/${uuidv4()}.${fileExtension}`;
    console.log('useProfile: Attempting to upload avatar to:', filePath);

    try {
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { cacheControl: '3600', upsert: true });

      if (uploadError) {
        console.error('useProfile: Error uploading avatar:', uploadError);
        return { success: false, error: uploadError };
      }
      console.log('useProfile: Avatar uploaded successfully.');

      const { data: publicUrlData } = supabase.storage.from('avatars').getPublicUrl(filePath);

      if (!publicUrlData.publicUrl) {
        console.error('useProfile: Could not get public URL for avatar.');
        return { success: false, error: new Error('Could not get public URL.') };
      }
      console.log('useProfile: Public avatar URL:', publicUrlData.publicUrl);

      const { success, error } = await updateProfile({ avatar_url: publicUrlData.publicUrl });

      if (success) {
        console.log('useProfile: Profile updated with new avatar URL.');
        return { success: true, publicUrl: publicUrlData.publicUrl };
      } else {
        console.error('useProfile: Failed to update profile with new avatar URL:', error);
        return { success: false, error };
      }
    } catch (error) {
      console.error('useProfile: Exception during avatar upload:', error);
      return { success: false, error: error as Error };
    }
  }, [authUser, updateProfile]);

  const deleteAvatar = useCallback(async () => {
    if (!authUser?.id || !profile?.avatar) {
      console.error('useProfile: User not authenticated or no avatar to delete.');
      return { success: false, error: new Error('User not authenticated or no avatar to delete.') };
    }

    const avatarUrl = profile.avatar;
    const pathSegments = avatarUrl.split('/');
    const fileName = pathSegments[pathSegments.length - 1];
    const filePath = `${authUser.id}/${fileName}`;
    console.log('useProfile: Attempting to delete avatar from:', filePath);

    try {
      const { error: removeError } = await supabase.storage.from('avatars').remove([filePath]);

      if (removeError) {
        console.error('useProfile: Error removing avatar from storage:', removeError);
        return { success: false, error: removeError };
      }
      console.log('useProfile: Avatar removed from storage.');

      const { success, error } = await updateProfile({ avatar_url: null });

      if (success) {
        console.log('useProfile: Profile updated to remove avatar URL.');
        return { success: true };
      } else {
        console.error('useProfile: Failed to update profile to remove avatar URL:', error);
        return { success: false, error };
      }
    } catch (error) {
      console.error('useProfile: Exception during avatar deletion:', error);
      return { success: false, error: error as Error };
    }
  }, [authUser, profile, updateProfile]);

  useEffect(() => {
    isMounted.current = true;
    if (authUser && !profile) {
      console.log('useProfile: authUser detected and no profile, calling fetchProfile.');
      fetchProfile(authUser.id);
    } else if (!authUser) {
      console.log('useProfile: No authUser, clearing profile.');
      setProfile(null);
    }

    return () => {
      isMounted.current = false;
      console.log('useProfile: Component unmounted, isMounted set to false.');
    };
  }, [authUser, profile, fetchProfile, setProfile]);

  return { profile, updateProfile, uploadAvatar, deleteAvatar };
}